import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Clock, MapPin, Phone, AppWindow } from 'lucide-react'; // Example icons

const AboutPage: React.FC = () => {
  const { t } = useTranslation();

  // Placeholder content - replace with actual museum data
  const missionText = t(
    'about.missionText',
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.'
  );
  const historyText = t(
    'about.historyText',
    'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.'
  );
  const hoursText = t(
    'about.hoursText',
    'Tuesday - Sunday: 10:00 AM - 6:00 PM\nMonday: Closed'
  );
  const locationText = t(
    'about.locationText',
    '123 Museum Street, Art City, AC 12345'
  );
  const contactText = t('about.contactText', '(123) 456-7890 | info@skmuseum.example');
  const appInfoText = t(
    'about.appInfoText',
    'This app provides information about our exhibits, rooms, and tours. Use it to enhance your visit or explore the museum remotely.'
  );

  return (
    <div className="container mx-auto p-4 py-8 md:p-8">
      <h1 className="mb-8 text-3xl font-bold md:text-4xl">
        {t('about.title', 'About SK Museum')}
      </h1>

      <div className="space-y-10">
        {/* Mission Section */}
        <section aria-labelledby="mission-heading">
          <h2
            id="mission-heading"
            className="mb-3 flex items-center text-2xl font-semibold"
          >
            <Info className="mr-2 h-6 w-6 text-blue-600" />
            {t('about.missionTitle', 'Our Mission')}
          </h2>
          <p className="text-gray-700 dark:text-gray-300">{missionText}</p>
        </section>

        {/* History Section */}
        <section aria-labelledby="history-heading">
          <h2
            id="history-heading"
            className="mb-3 flex items-center text-2xl font-semibold"
          >
            <Info className="mr-2 h-6 w-6 text-blue-600" /> {/* Consider a different icon */}
            {t('about.historyTitle', 'Our History')}
          </h2>
          <p className="text-gray-700 dark:text-gray-300">{historyText}</p>
        </section>

        {/* Visit Information Section */}
        <section aria-labelledby="visit-heading">
          <h2
            id="visit-heading"
            className="mb-4 text-2xl font-semibold"
          >
            {t('about.visitTitle', 'Visit Us')}
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            {/* Hours */}
            <div className="flex items-start">
              <Clock className="mr-3 mt-1 h-5 w-5 flex-shrink-0 text-blue-600" />
              <div>
                <h3 className="font-medium">
                  {t('about.hoursTitle', 'Opening Hours')}
                </h3>
                <p className="whitespace-pre-line text-sm text-gray-600 dark:text-gray-400">
                  {hoursText}
                </p>
              </div>
            </div>
            {/* Location */}
            <div className="flex items-start">
              <MapPin className="mr-3 mt-1 h-5 w-5 flex-shrink-0 text-blue-600" />
              <div>
                <h3 className="font-medium">
                  {t('about.locationTitle', 'Location')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {locationText}
                </p>
                {/* Add Map Link/Embed later if needed */}
              </div>
            </div>
            {/* Contact */}
            <div className="flex items-start">
              <Phone className="mr-3 mt-1 h-5 w-5 flex-shrink-0 text-blue-600" />
              <div>
                <h3 className="font-medium">
                  {t('about.contactTitle', 'Contact')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {contactText}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* About App Section */}
        <section aria-labelledby="app-info-heading">
          <h2
            id="app-info-heading"
            className="mb-3 flex items-center text-2xl font-semibold"
          >
            <AppWindow className="mr-2 h-6 w-6 text-blue-600" />
            {t('about.appInfoTitle', 'About This App')}
          </h2>
          <p className="text-gray-700 dark:text-gray-300">{appInfoText}</p>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
